import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Checkbox } from '../../ui/checkbox';
import { Badge } from '../../ui/badge';
import { Upload, FileText, Loader2, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { priceImportAPI } from '../../../lib/api';
import { toast } from 'sonner';

/**
 * Modal d'import des tarifs hebdomadaires depuis un PDF.
 * - Etape 1: choix du PDF
 * - Etape 2: previsualisation (matches a mettre a jour, nouveaux a creer, ignores)
 * - Etape 3: application des changements (POST /apply)
 */
export const PriceImportPDF = ({ open, onOpenChange, onSuccess, categories = [] }) => {
    const fileInputRef = useRef(null);
    const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'applying' | 'done'
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState(null);
    // Decisions: Set d'IDs de matches inclus + Set d'index to_create inclus + edits sur to_create
    const [includedMatchIds, setIncludedMatchIds] = useState(new Set());
    const [includedCreateIdx, setIncludedCreateIdx] = useState(new Set());
    const [createEdits, setCreateEdits] = useState({}); // { idx: { name, price, category_id, unit } }
    const [result, setResult] = useState(null);

    const reset = () => {
        setStep('upload');
        setLoading(false);
        setPreview(null);
        setIncludedMatchIds(new Set());
        setIncludedCreateIdx(new Set());
        setCreateEdits({});
        setResult(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleClose = (v) => {
        if (!v) reset();
        onOpenChange(v);
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.pdf')) {
            toast.error('Veuillez selectionner un fichier PDF');
            return;
        }
        setLoading(true);
        const fd = new FormData();
        fd.append('file', file);
        try {
            toast.info('Extraction des tarifs en cours (peut prendre 10-30s)...');
            const res = await priceImportAPI.preview(fd);
            setPreview(res.data);
            // Par defaut: tout est inclus
            setIncludedMatchIds(new Set(res.data.matches.map(m => m.match_id)));
            setIncludedCreateIdx(new Set(res.data.to_create.map((_, i) => i)));
            setStep('preview');
            toast.success(`${res.data.extracted_count} produits extraits`);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.detail || "Erreur lors de l'extraction du PDF");
        } finally {
            setLoading(false);
        }
    };

    const toggleMatch = (id) => {
        setIncludedMatchIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleCreate = (idx) => {
        setIncludedCreateIdx(prev => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx); else next.add(idx);
            return next;
        });
    };
    const editCreate = (idx, field, value) => {
        setCreateEdits(prev => ({ ...prev, [idx]: { ...(prev[idx] || {}), [field]: value } }));
    };

    const handleApply = async () => {
        if (!preview) return;
        const updates = preview.matches
            .filter(m => includedMatchIds.has(m.match_id))
            .map(m => ({ id: m.match_id, price: m.pdf_price }));
        const creates = preview.to_create
            .map((c, idx) => ({ ...c, ...(createEdits[idx] || {}), _idx: idx }))
            .filter(c => includedCreateIdx.has(c._idx))
            .map(({ _idx, ...rest }) => ({
                ...rest,
                price: typeof rest.price === 'string' ? parseFloat(rest.price.replace(',', '.')) : rest.price,
            }));

        if (updates.length === 0 && creates.length === 0) {
            toast.error('Aucune modification selectionnee');
            return;
        }
        setStep('applying');
        try {
            const res = await priceImportAPI.apply({ updates, creates });
            setResult(res.data);
            setStep('done');
            toast.success(res.data.message || 'Import termine');
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.detail || "Erreur lors de l'application");
            setStep('preview');
        }
    };

    const fmt = (v) => (v == null ? '-' : Number(v).toFixed(2) + ' EUR');

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-sand" data-testid="price-import-dialog">
                <DialogHeader>
                    <DialogTitle className="text-jungle font-heading flex items-center gap-2">
                        <FileText className="w-5 h-5" /> Importer Tarifs PDF
                    </DialogTitle>
                    <DialogDescription>
                        Telechargez le PDF des tarifs hebdomadaires (colonne <strong>PRIX PRIXDEAL</strong>). L'IA extrait, propose des correspondances avec vos produits, et vous validez avant application.
                    </DialogDescription>
                </DialogHeader>

                {step === 'upload' && (
                    <div className="py-8">
                        <div
                            onClick={() => !loading && fileInputRef.current?.click()}
                            className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-colors ${loading ? 'border-jungle/20 bg-jungle/5' : 'border-jungle/30 hover:border-jungle hover:bg-jungle/5'}`}
                            data-testid="price-import-drop-zone"
                        >
                            {loading ? (
                                <div className="flex flex-col items-center gap-3">
                                    <Loader2 className="w-12 h-12 text-jungle animate-spin" />
                                    <p className="text-jungle font-medium">Analyse du PDF par l'IA en cours...</p>
                                    <p className="text-sm text-muted-foreground">Cela peut prendre 10 a 30 secondes</p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-3">
                                    <Upload className="w-12 h-12 text-jungle/60" />
                                    <p className="text-jungle font-medium">Cliquez pour selectionner un PDF</p>
                                    <p className="text-sm text-muted-foreground">Format attendu : tableau avec colonne "PRIX PRIXDEAL"</p>
                                </div>
                            )}
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,application/pdf"
                            onChange={handleFile}
                            className="hidden"
                            data-testid="price-import-file-input"
                        />
                    </div>
                )}

                {step === 'preview' && preview && (
                    <div className="space-y-6 py-4">
                        <div className="flex flex-wrap gap-3">
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">{preview.extracted_count} extraits</Badge>
                            <Badge className="bg-blue-100 text-blue-700 border-blue-300">{preview.matches.length} a mettre a jour</Badge>
                            <Badge className="bg-orange-100 text-orange-700 border-orange-300">{preview.to_create.length} nouveaux</Badge>
                            <Badge className="bg-gray-100 text-gray-700 border-gray-300">{preview.skipped.length} ignores</Badge>
                        </div>

                        {/* Matches */}
                        {preview.matches.length > 0 && (
                            <section data-testid="price-import-matches">
                                <h3 className="font-heading text-jungle text-lg flex items-center gap-2 mb-3">
                                    <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Mises a jour de prix ({preview.matches.length})
                                </h3>
                                <div className="overflow-x-auto rounded-xl border border-jungle/10 bg-white">
                                    <table className="w-full text-sm">
                                        <thead className="bg-jungle/5 text-jungle">
                                            <tr>
                                                <th className="p-2 w-10"></th>
                                                <th className="p-2 text-left">Nom PDF</th>
                                                <th className="p-2 text-left">Produit cible</th>
                                                <th className="p-2 text-right">Ancien prix</th>
                                                <th className="p-2 text-right">Nouveau prix</th>
                                                <th className="p-2 text-center">Score</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {preview.matches.map(m => {
                                                const checked = includedMatchIds.has(m.match_id);
                                                const lowConf = m.score < 0.75;
                                                return (
                                                    <tr key={m.match_id} className={`border-t border-jungle/5 ${checked ? '' : 'opacity-50'}`}>
                                                        <td className="p-2">
                                                            <Checkbox checked={checked} onCheckedChange={() => toggleMatch(m.match_id)} data-testid={`match-cb-${m.match_id}`} />
                                                        </td>
                                                        <td className="p-2">{m.pdf_name}{m.pdf_note && <span className="text-xs text-muted-foreground"> ({m.pdf_note})</span>}</td>
                                                        <td className="p-2 font-medium text-jungle">{m.match_name}</td>
                                                        <td className="p-2 text-right text-muted-foreground">{fmt(m.current_price)}</td>
                                                        <td className="p-2 text-right font-semibold text-emerald-700">{fmt(m.pdf_price)}</td>
                                                        <td className="p-2 text-center">
                                                            <Badge className={lowConf ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                                                                {(m.score * 100).toFixed(0)}%
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">
                                    Astuce : decoche les correspondances incertaines (score &lt; 75%) si elles te semblent fausses.
                                </p>
                            </section>
                        )}

                        {/* To create */}
                        {preview.to_create.length > 0 && (
                            <section data-testid="price-import-creates">
                                <h3 className="font-heading text-jungle text-lg flex items-center gap-2 mb-3">
                                    <AlertTriangle className="w-5 h-5 text-orange-600" /> Nouveaux produits a creer ({preview.to_create.length})
                                </h3>
                                <div className="overflow-x-auto rounded-xl border border-jungle/10 bg-white">
                                    <table className="w-full text-sm">
                                        <thead className="bg-jungle/5 text-jungle">
                                            <tr>
                                                <th className="p-2 w-10"></th>
                                                <th className="p-2 text-left">Nom</th>
                                                <th className="p-2 text-left">Categorie</th>
                                                <th className="p-2 text-left">Unite</th>
                                                <th className="p-2 text-right">Prix</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {preview.to_create.map((c, idx) => {
                                                const edit = createEdits[idx] || {};
                                                const checked = includedCreateIdx.has(idx);
                                                return (
                                                    <tr key={idx} className={`border-t border-jungle/5 ${checked ? '' : 'opacity-50'}`}>
                                                        <td className="p-2">
                                                            <Checkbox checked={checked} onCheckedChange={() => toggleCreate(idx)} data-testid={`create-cb-${idx}`} />
                                                        </td>
                                                        <td className="p-2">
                                                            <Input value={edit.name ?? c.name} onChange={(e) => editCreate(idx, 'name', e.target.value)} className="h-8" />
                                                        </td>
                                                        <td className="p-2">
                                                            <select
                                                                value={edit.category_id ?? c.category_id}
                                                                onChange={(e) => editCreate(idx, 'category_id', e.target.value)}
                                                                className="h-8 border border-input rounded-md px-2 text-sm w-full"
                                                            >
                                                                {categories.map(cat => (
                                                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        <td className="p-2">
                                                            <Input value={edit.unit ?? c.unit} onChange={(e) => editCreate(idx, 'unit', e.target.value)} className="h-8 w-24" />
                                                        </td>
                                                        <td className="p-2 text-right">
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                value={edit.price ?? c.price}
                                                                onChange={(e) => editCreate(idx, 'price', e.target.value)}
                                                                className="h-8 w-24 text-right ml-auto"
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        )}

                        {/* Skipped */}
                        {preview.skipped.length > 0 && (
                            <section>
                                <h3 className="font-heading text-jungle text-lg flex items-center gap-2 mb-3">
                                    <XCircle className="w-5 h-5 text-gray-500" /> Ignores ({preview.skipped.length})
                                </h3>
                                <ul className="text-sm text-muted-foreground space-y-1">
                                    {preview.skipped.map((s, i) => (
                                        <li key={i}>- <strong>{s.name}</strong> : {s.reason}</li>
                                    ))}
                                </ul>
                            </section>
                        )}
                    </div>
                )}

                {step === 'applying' && (
                    <div className="py-12 flex flex-col items-center gap-3">
                        <Loader2 className="w-10 h-10 text-jungle animate-spin" />
                        <p className="text-jungle">Application des modifications...</p>
                    </div>
                )}

                {step === 'done' && result && (
                    <div className="py-8 text-center space-y-3">
                        <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto" />
                        <h3 className="font-heading text-2xl text-jungle">Import termine !</h3>
                        <p className="text-muted-foreground">
                            <strong>{result.updated_count}</strong> prix mis a jour, <strong>{result.created_count}</strong> produit(s) cree(s).
                        </p>
                        {result.errors?.length > 0 && (
                            <div className="text-sm text-amber-700 bg-amber-50 rounded-xl p-3 inline-block">
                                {result.errors.length} erreur(s) - voir la console
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter className="gap-2">
                    {step === 'upload' && (
                        <Button variant="outline" onClick={() => handleClose(false)} className="rounded-xl">Annuler</Button>
                    )}
                    {step === 'preview' && (
                        <>
                            <Button variant="outline" onClick={() => { reset(); }} className="rounded-xl" data-testid="price-import-restart-btn">Recommencer</Button>
                            <Button onClick={handleApply} className="bg-jungle hover:bg-jungle/90 text-sand rounded-xl" data-testid="price-import-apply-btn">
                                Appliquer ({includedMatchIds.size + includedCreateIdx.size})
                            </Button>
                        </>
                    )}
                    {step === 'done' && (
                        <Button onClick={() => handleClose(false)} className="bg-jungle hover:bg-jungle/90 text-sand rounded-xl" data-testid="price-import-close-btn">Fermer</Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
